import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { KTSVG } from '../../../../_metronic/helpers'
import { DailyPositionReportRecord, blankMovementReportRow } from '../core/_dprModels'
import { getDprList, createDpr, updateDpr, deleteDpr, getVesselList } from '../core/_requests'
import AddDprModal from './AddDprModal'
import ViewDprModal from './ViewDprModal'
import { useAuth } from '../../auth'
import { OP } from '../core/operationPermissions'
import { useOperationsAccess } from '../core/useOperationsAccess'
import { toast, ToastContainer } from 'react-toastify'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels, NO_ACCESSIBLE_VESSELS_MESSAGE } from '../../../shared/access/accessibleVessels'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'

/* ───────── date formatter ───────── */
const formatDateValues = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/* ───────── status badge ───────── */
const renderStatusBadge = (status: string) => {
  switch (status) {
    case 'DRAFT':
      return <span className='badge badge-light-secondary'>Draft</span>
    case 'SUBMITTED':
      return <span className='badge badge-light-warning'>Submitted</span>
    case 'REVIEWED':
      return <span className='badge badge-light-success'>Reviewed</span>
    case 'REJECTED':
      return <span className='badge badge-light-danger'>Rejected</span>
    default:
      return <span className='badge badge-light'>{status}</span>
  }
}

/* ── map backend response to frontend record ── */
const mapResponseToRecord = (r: any): DailyPositionReportRecord => ({
  id: r.id,
  vesselId: r.vesselId,
  createdByUserId: Number(
    r.createdByUserId
      ?? r.createdBy?.id
      ?? r.createdById
      ?? r.createdByUser?.id
      ?? r.userId
      ?? 0
  ) || undefined,
  reportDate: r.reportDate ?? '',
  vesselName: r.vesselName ?? '',
  voyageNo: r.voyageNo ?? '',
  portOrSea: r.portOrSea ?? 'SEA',
  fromLocation: r.fromLocation ?? '',
  toLocation: r.toLocation ?? '',
  eta: r.eta ?? '',
  draftFwd: r.draftFwd ?? '',
  draftAft: r.draftAft ?? '',
  weather: r.weather ?? '',
  wind: r.wind ?? '',
  seaState: r.seaState ?? '',
  foConsumptionRows: r.foConsumptionRows ?? [],
  machineryFoConsumptionRows: r.machineryFoConsumptionRows ?? r.machineryFoConsumption ?? [],
  lubeOilStatusRows: r.lubeOilStatusRows ?? r.lubeOilStatus ?? [],
  dlDate: r.dlDate ?? '',
  dlTime: r.dlTime ?? '',
  dlLocation: r.dlLocation ?? '',
  dlPosition: r.dlPosition ?? '',
  activities: (r.activities ?? []).map((a: any) => ({
    ...a,
    id: a.id ?? Date.now(),
  })),
  activitySummary: r.activitySummary ?? {
    speedsUpto6: '', speedsAbove6: '', inDpMode: '', atAnchorageBerth: '',
    downTime: '', othersSpecify: '', othersLabel: '', total: '', fuelConsumedIn24Hrs: '',
  },
  robStatus: r.robStatus ?? [],
  weatherRows: r.weatherRows ?? [],
  tides: r.tides ?? {
    amLw: '', amHw: '', pmLw: '', pmHw: '',
    amLwHeight: '', amHwHeight: '', pmLwHeight: '', pmHwHeight: '',
    sunrise: '', sunset: '',
  },
  downtimeRows: (r.downtimeRows ?? []).map((d: any) => ({
    ...d,
    id: d.id ?? Date.now(),
  })),
  pobRows: r.pobRows ?? [],
  safetyLeft: r.safetyLeft ?? [],
  safetyRight: r.safetyRight ?? [],
  equipmentRows: r.equipmentRows ?? [],
  passengerRows: r.passengerRows ?? [],
  status: r.status ?? 'DRAFT',
  engineTemp: r.engineTemp ?? {
    params: r.engineParamRows ?? [],
    exhTemps: r.exhTempRows ?? [],
  },
  engineRunningHours: r.engineRunningHours ?? [],
  movementReport: r.movementReport ?? r.movementReportRows?.[0] ?? blankMovementReportRow(),
  createdByRank: r.createdByRank ?? '',
  createdAt: r.createdAt ?? new Date().toISOString(),
})

const resolveLubeClosingStock = (row: any) => {
  const explicitClosing = row.closingStock ?? row.closing
  if (explicitClosing !== undefined && explicitClosing !== null && explicitClosing !== '' && explicitClosing !== '—') return explicitClosing

  const openingStock = parseFloat(row.openingStock)
  if (!isNaN(openingStock)) {
    const received = parseFloat(row.received) || 0
    const consumed = parseFloat(row.consumedByVessel) || 0
    return (openingStock + received - consumed).toFixed(3).replace(/\.?0+$/, '')
  }
  return undefined
}



const ROWS_PER_PAGE = 10

const DailyPositionReport: React.FC = () => {
  const {currentUser} = useAuth()
  const {opCan, permissionsLoaded} = useOperationsAccess()
  const canReadDpr = opCan(OP.READ_DAILY_PROGRESS_REPORT)
  const canAddDpr = opCan(OP.CREATE_DAILY_PROGRESS_REPORT)
  const canEditDpr = opCan(OP.UPDATE_DAILY_PROGRESS_REPORT)
  const canDownloadDpr = opCan(OP.DOWNLOAD_DAILY_PROGRESS_REPORT)
  const currentUserId = Number((currentUser as any)?.id ?? 0)
  const currentVesselId = Number((currentUser as any)?.vessel?.id ?? 0)
  const currentVesselName = String((currentUser as any)?.vessel?.fleet_name ?? '')
  const isVesselLogin = currentVesselId > 0
  const roleId = Number((currentUser as any)?.role?.id ?? 0)
  const roleType = String((currentUser as any)?.role?.roleType ?? (currentUser as any)?.roleType ?? '').toUpperCase()
  const roleName = String(
    (currentUser as any)?.role?.roleName ?? (currentUser as any)?.role?.name ?? (currentUser as any)?.roleName ?? ''
  ).toUpperCase()
  const normalizedRoleType = roleType.replace(/[^A-Z0-9]/g, '')
  const normalizedRoleName = roleName.replace(/[^A-Z0-9]/g, '')
  const isOperator = roleId === 6 || normalizedRoleType.includes('OPERATOR') || normalizedRoleName.includes('OPERATOR')
  const isCompanyGroupAdmin =
    roleId === 5 || roleId === 7 ||
    (normalizedRoleType.includes('COMPANY') && normalizedRoleType.includes('GROUP') && normalizedRoleType.includes('ADMIN')) ||
    (normalizedRoleName.includes('COMPANY') && normalizedRoleName.includes('GROUP') && normalizedRoleName.includes('ADMIN')) || 
    (normalizedRoleType.includes('COMPANY') && normalizedRoleType.includes('ADMIN') ) ||
    (normalizedRoleName.includes('COMPANY') && normalizedRoleName.includes('ADMIN') )
  const isSuperAdmin =
    roleId === 1 ||
    (normalizedRoleType.includes('SUPER') && normalizedRoleType.includes('ADMIN')) ||
    (normalizedRoleName.includes('SUPER') && normalizedRoleName.includes('ADMIN'))
  const canSubmittedEdit = isSuperAdmin || isCompanyGroupAdmin
  const canSeeAllVesselsInDropdown = isSuperAdmin || isCompanyGroupAdmin || isOperator
  const canDeleteReport = opCan(OP.DELETE_DAILY_PROGRESS_REPORT)
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  const draftOwnerStorageKey = (reportId: number) => `dpr-draft-owner:${reportId}`

  const isDraftVisibleToCurrentUser = (report: DailyPositionReportRecord) => {
    if (report.status !== 'DRAFT') return true

    // For DRAFT: show only if it belongs to current user's side
    if (isVesselLogin) {
      // Vessel users see only drafts from their vessel
      return Number(report.vesselId ?? 0) === currentVesselId
    }

    // Non-vessel users (superadmin, operator, company admin) see only drafts created by users with the same role
    const reportCreatorRole = String(report.createdByRank ?? '').toUpperCase()
    const currentUserRole = roleName || roleType
    return reportCreatorRole === currentUserRole
  }
  /* ── data ── */
  const [reports, setReports] = useState<DailyPositionReportRecord[]>([])
  const [allVesselOptions, setAllVesselOptions] = useState<Array<{id: number; name: string; companyGroupAdminId?: number; companyAdminId?: number}>>([])
  const [loading, setLoading] = useState(false)

  /* ── filters ── */
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [vesselFilter, setVesselFilter] = useState<string>('ALL')
  const vesselFilterRef = useRef<HTMLSelectElement | null>(null)

  /* ── sorting ── */
  const [sortConfig, setSortConfig] = useState<{
    key: keyof DailyPositionReportRecord | null
    direction: 'asc' | 'desc'
  }>({ key: null, direction: 'asc' })

  const handleSort = (key: keyof DailyPositionReportRecord) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  /* ── pagination ── */
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  /* ── modals ── */
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isViewOpen, setIsViewOpen] = useState(false)
  const [editData, setEditData] = useState<DailyPositionReportRecord | null>(null)
  const [viewData, setViewData] = useState<DailyPositionReportRecord | null>(null)

  /* ── fetch from API ── */
  const fetchReports = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getDprList()
      setReports(data.map(mapResponseToRecord))
    } catch (err: any) {
      console.error('Failed to load DPRs:', err)
      toast.error(getApiErrorMessage(err, 'Failed to load Daily Position Reports.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!permissionsLoaded || !canReadDpr) return
    fetchReports()
  }, [permissionsLoaded, canReadDpr, fetchReports])

  useEffect(() => {
    if (isVesselLogin || !canSeeAllVesselsInDropdown) {
      setAllVesselOptions([])
      return
    }

    let mounted = true
    fetchAccessibleVesselsForCurrentUser()
      .then((vessels: any[]) => {
        if (!mounted) return
        const mapped = normalizeAccessibleVessels(vessels ?? [])
          .map((v: any) => ({
            id: Number(v?.id ?? 0),
            name: String(v?.fleet_name ?? '').trim(),
            companyGroupAdminId: Number(v.companyGroupAdminId ?? v.companyGroupAdmin?.id ?? v.companyGroupId) || undefined,
            companyAdminId: Number(v.companyAdminId ?? v.companyAdmin?.id ?? v.companyId) || undefined,
          }))
          .filter((v) => v.id > 0 && v.name !== '')
          .sort((a, b) => a.name.localeCompare(b.name))

        setAllVesselOptions(mapped)
      })
      .catch((err) => {
        console.error('Failed to load vessel list for DPR filter:', err)
        if (mounted) setAllVesselOptions([])
      })

    return () => {
      mounted = false
    }
  }, [isVesselLogin, canSeeAllVesselsInDropdown])

  // Sync global header vessel filter → local vesselFilter
  useEffect(() => {
    const gv = globalState.vessel
    if (gv && gv.id) {
      setVesselFilter(String(gv.fleet_name ?? gv.name ?? ''))
    }
  }, [globalState.vessel])

  /* ── derived info ── */
  // Vessel IDs filtered by global company filter
  const companyFilteredVesselIds = useMemo(() => {
    if (!globalState.company) return null // no filter
    const cg = globalState.company
    return new Set(
      allVesselOptions
        .filter((v) => {
          if (cg.type === 'company_group') {
            return v.companyGroupAdminId === cg.id
          }
          return v.companyAdminId === cg.id
        })
        .map((v) => v.id)
    )
  }, [allVesselOptions, globalState.company])

  const uniqueVessels = Array.from(new Set(reports.map((r) => r.vesselName))).sort()
  const vesselDropdownOptions = canSeeAllVesselsInDropdown
    ? Array.from(new Set(
        allVesselOptions
          .filter((v) => !companyFilteredVesselIds || companyFilteredVesselIds.has(v.id))
          .map((v) => v.name)
      ))
    : uniqueVessels



  const selectedVesselOption = vesselFilter !== 'ALL'
    ? allVesselOptions.find((v) => v.name === vesselFilter)
    : undefined
  const selectedVesselRecord = vesselFilter !== 'ALL'
    ? reports.find((r) => r.vesselName === vesselFilter && Number(r.vesselId ?? 0) > 0)
    : undefined
  const selectedVesselId = isVesselLogin
    ? currentVesselId
    : Number(selectedVesselOption?.id ?? selectedVesselRecord?.vesselId ?? 0) || undefined
  const selectedVesselName = isVesselLogin
    ? currentVesselName
    : vesselFilter !== 'ALL' ? vesselFilter : undefined

  /* ── filtering & sorting ── */
  const processed = (() => {
    const allowedVesselIds = globalState.company
      ? new Set(globalVessels.map((v: any) => Number(v.id)))
      : null

    let list = reports
      .filter((r) => isDraftVisibleToCurrentUser(r))
      .filter((r) => {
        const matchSearch =
          !searchTerm ||
          r.vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.voyageNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.reportDate.includes(searchTerm)
        const matchStatus = statusFilter === 'ALL' || r.status === statusFilter

        // Global filter: vessel
        let matchGlobalVessel = true
        if (globalState.vessel?.id != null) {
          matchGlobalVessel = Number(r.vesselId ?? 0) === Number(globalState.vessel.id)
        }

        // Global filter: company
        let matchGlobalCompany = true
        if (globalState.company && allowedVesselIds) {
          const rVId = Number(r.vesselId ?? 0)
          if (rVId > 0 && allowedVesselIds.has(rVId)) {
            matchGlobalCompany = true
          } else {
            const rCgId = (r as any).companyGroupAdminId ?? (r as any).companyGroupId ?? (r as any).companyGroupAdmin?.id
            const rCaId = (r as any).companyAdminId ?? (r as any).companyId ?? (r as any).companyAdmin?.id
            if (globalState.company.type === 'company_group') {
              matchGlobalCompany = rCgId != null && Number(rCgId) === globalState.company.id
            } else {
              matchGlobalCompany = rCaId != null && Number(rCaId) === globalState.company.id
            }
          }
        }

        const matchVessel = isVesselLogin
          ? Number(r.vesselId ?? 0) === currentVesselId
          : (vesselFilter === 'ALL'
              || (selectedVesselId
                  ? Number(r.vesselId ?? 0) === Number(selectedVesselId)
                  : r.vesselName === vesselFilter))

        return matchSearch && matchStatus && matchVessel && matchGlobalVessel && matchGlobalCompany
      })

    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        const valA = (a[sortConfig.key!] ?? '').toString().toLowerCase()
        const valB = (b[sortConfig.key!] ?? '').toString().toLowerCase()
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return list
  })()

  /* ── pagination calc ── */
  const totalPages = Math.max(1, Math.ceil(processed.length / rowsPerPage))
  const paginated = processed.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  )

  /* ── handlers ── */
  const handleAdd = () => {
    toast.dismiss() // Clear any lingering toasts to prevent confusion
    if (vesselFilter === 'ALL' && !isVesselLogin) {
      toast.warning('Please select a vessel from the dropdown to add a DPR.', { 
        position: 'top-center',
        toastId: `add-dpr-vessel-warning-${Date.now()}`
      })
      return
    }
    setEditData(null)
    setIsAddOpen(true)
  }

  const handleEdit = (item: DailyPositionReportRecord) => {
    toast.dismiss()
    setEditData(item)
    setIsAddOpen(true)
  }

  const handleView = (item: DailyPositionReportRecord) => {
    setViewData(item)
    setIsViewOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteDpr(id)
      toast.success('DPR deleted successfully.', { position: 'top-center' })
      await fetchReports()
    } catch (err: any) {
      console.error('Failed to delete DPR:', err)
      toast.error('Failed to delete DPR. Please try again.', { position: 'top-center' })
    }
  }

  const handleFinalDelete = async (id: number) => {
    try {
      await deleteDpr(id)
      fetchReports()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  const handleReportDownload = async (item: DailyPositionReportRecord) => {
    const safe = (value?: string | number | null) => {
      const str = String(value ?? '')
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }
    const normalizeExhTempLabel = (label?: string) => {
      const normalized = String(label ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
      switch (normalized) {
        case 'M.E PORT':
        case 'M.E. PORT':
          return 'M/E(P)'
        case 'M.E LOG. PORT':
        case 'M.E LOG PORT':
        case 'M.E. LOG. PORT':
          return 'M/E(S)'
        case 'M.E STBD':
        case 'M.E. STBD':
          return 'AUX(1)'
        case 'M.E LOG. STBD':
        case 'M.E LOG STBD':
        case 'M.E. LOG. STBD':
          return 'AUX(2)'
        default:
          return label || '—'
      }
    }

    const formatShortDate = (value?: string | number | null) => {
      const s = String(value ?? '').trim()
      if (!s) return ''
      const d = new Date(s)
      if (isNaN(d.getTime())) return s
      const day = d.getDate()
      const month = d.getMonth() + 1
      const year = String(d.getFullYear()).slice(-2)
      return `${day}/${month}/${year}`
    }

    const asNumber = (value?: string | number | null) => {
      const n = Number(value ?? 0)
      return Number.isFinite(n) ? n : 0
    }

    const rowTotals = {
      previous: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.previous), 0),
      paxIn: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.paxIn), 0),
      paxOut: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.paxOut), 0),
      total: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.total), 0),
      breakfast: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.mealsBreakfast), 0),
      lunch: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.mealsLunch), 0),
      dinner: (item.pobRows || []).reduce((sum, r) => sum + asNumber(r.mealsDinner), 0),
    }

    const buildRows = (rows: string[][], fallbackColSpan: number, fallbackText: string) =>
      rows.length
        ? rows
            .map(
              (cells) =>
                `<tr>${cells
                  .map(
                    (cell) =>
                      `<td style="border:1px solid #cfd4dc; padding:6px; vertical-align:top;">${cell}</td>`
                  )
                  .join('')}</tr>`
            )
            .join('')
        : `<tr><td colspan="${fallbackColSpan}" style="border:1px solid #cfd4dc; padding:6px;">${fallbackText}</td></tr>`

    const getExhTempMinValue = (row: any) => {
      const values = [
        row?.unit1,
        row?.unit2,
        row?.unit3,
        row?.unit4,
        row?.unit5,
        row?.unit6,
        row?.unit7,
        row?.unit8,
      ]
        .map((value) => String(value ?? '').trim())
        .filter((value) => value !== '')
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))

      if (!values.length) return ''
      const average = values.reduce((sum, value) => sum + value, 0) / values.length
      return Number(average.toFixed(3)).toString()
    }

    try {
      const pdfContainer = document.createElement('div')
      pdfContainer.style.padding = '16px'
      pdfContainer.style.fontFamily = 'Arial, sans-serif'
      pdfContainer.style.fontSize = '11px'
      pdfContainer.style.color = '#111'

      const commonTableStyle = 'width:100%; border-collapse:collapse; margin-bottom:16px;'
      const commonHeadCell =
        'border:1px solid #cfd4dc; padding:6px; background:#eaf3ff; text-align:left;'

      pdfContainer.innerHTML = `
        <style>
          .dpr-pdf-page {
            page-break-after: always;
            break-after: page;
          }
          .dpr-pdf-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .dpr-pdf-page table thead th {
            background: #eaf3ff !important;
          }
          .dpr-pdf-page table tbody tr td {
            background: #ffffff !important;
          }
          .dpr-pdf-page table tbody tr:nth-child(odd) td,
          .dpr-pdf-page table tbody tr:nth-child(even) td {
            background: #ffffff !important;
          }
        </style>
        <div class="dpr-pdf-page">
        <h2 style="margin: 0 0 12px;">Daily Progress Report</h2>
        <h3 style="margin: 0 0 8px;">1. Ships Location</h3>
        <table style="${commonTableStyle}; font-size:11px;">
          <tbody>
            <tr>
              <td style="font-weight:700; background:#f5f7fa; width:18%;">Report Date :</td>
              <td style="width:32%;">${safe(item.reportDate || '—')}</td>
              <td style="font-weight:700; background:#f5f7fa; width:18%;">Vessel Name:</td>
              <td style="width:32%;">${safe(item.vesselName || '—')}</td>
            </tr>
            <tr>
              <td style="font-weight:700; background:#f5f7fa;">Voyage No :</td>
              <td>${safe(item.voyageNo || '—')}</td>
              <td style="background:#ffffff;"></td>
              <td style="background:#ffffff;"></td>
            </tr>
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">1.1 Position</h3>
        <table style="${commonTableStyle}; font-size:11px;">
          <tbody>
            <tr>
              <td style="font-weight:700; background:#f5f7fa; width:12%;">Date :</td>
              <td style="width:38%;">${safe(item.dlDate || '—')}</td>
              <td style="font-weight:700; background:#f5f7fa; width:12%;">Location:</td>
              <td style="width:38%;">${safe(item.dlLocation || '—')}</td>
            </tr>
            <tr>
              <td style="font-weight:700; background:#f5f7fa;">Time :</td>
              <td>${safe(item.dlTime || '—')}</td>
              <td style="font-weight:700; background:#f5f7fa;">Position:</td>
              <td style="white-space:normal; word-break:break-word;">${safe(item.dlPosition || '—')}</td>
            </tr>
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">2. FO Consumption</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell} width: 8%;padding: 6px 2px;">Date</th>
              <th style="${commonHeadCell}">Above 6 Kts</th>
              <th style="${commonHeadCell}">Above 6 Cons</th>
              <th style="${commonHeadCell}">Steaming Hrs</th>
              <th style="${commonHeadCell}">Steaming Cons</th>
              <th style="${commonHeadCell}">Platform DP Hrs</th>
              <th style="${commonHeadCell}">Platform DP Cons</th>
              <th style="${commonHeadCell}">At Anch Hrs</th>
              <th style="${commonHeadCell}">At Anch Cons</th>
              <th style="${commonHeadCell}">At Berth Hrs</th>
              <th style="${commonHeadCell}">At Berth Cons</th>
              <th style="${commonHeadCell}">Total FO Cons</th>
              <th style="${commonHeadCell}">Total Hr</th>
              <th style="${commonHeadCell} width:120px; min-width:120px;">Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.foConsumptionRows || []).map((row) => [
                safe(formatShortDate(row.date)),
                safe(row.above6Hrs),
                safe(row.above6Cons),
                safe(row.steamingHrs),
                safe(row.steamingCons),
                safe(row.platformDpHrs),
                safe(row.platformDpCons),
                safe(row.atAnchorageHrs),
                safe(row.atAnchorageCons),
                safe(row.atBerthHrs),
                safe(row.atBerthCons),
                safe(row.totalFoCons),
                safe(row.totalHr1),
                `<div style="min-width:120px; white-space:normal; word-break:break-word;">${safe(row.remarks)}</div>`,
              ]),
              14,
              'No FO consumption records'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">2.1 Machinery F.O. Consumption</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Machinery</th>
              <th style="${commonHeadCell}">FO Consumption (KL)</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.machineryFoConsumptionRows || []).map((row) => [
                safe(row.machinery),
                safe(row.foConsumption),
              ]),
              2,
              'No machinery FO consumption rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">3. Activity</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Local Time</th>
              <th style="${commonHeadCell}">Description</th>
              <th style="${commonHeadCell}">Position</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.activities || []).map((a) => [
                `${safe(a.localTimeFrom)} - ${safe(a.localTimeTo)}`,
                safe(a.description) || '-',
                safe(a.position) || '-',
              ]),
              3,
              'No activity records'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">4. Activity Summary</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Speeds ≤6 kn</th>
              <th style="${commonHeadCell}">Speeds &gt;6 kn</th>
              <th style="${commonHeadCell}">In DP Mode</th>
              <th style="${commonHeadCell}">At Anchorage / Berth</th>
              <th style="${commonHeadCell}">Down Time</th>
              <th style="${commonHeadCell}">${safe(item.activitySummary?.othersLabel || 'Others')}</th>
              <th style="${commonHeadCell}">Total</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              [[
                safe(item.activitySummary?.speedsUpto6),
                safe(item.activitySummary?.speedsAbove6),
                safe(item.activitySummary?.inDpMode),
                safe(item.activitySummary?.atAnchorageBerth),
                safe(item.activitySummary?.downTime),
                safe(item.activitySummary?.othersSpecify),
                safe(item.activitySummary?.total),
              ]],
              7,
              'No activity summary data'
            )}
          </tbody>
        </table>
        <table style="${commonTableStyle}">
          <tbody>
            <tr><td style="font-weight:700; width:30%;">Fuel Consumed in 24 Hrs</td><td>${safe(item.activitySummary?.fuelConsumedIn24Hrs)}</td></tr>
          </tbody>
        </table>

        </div>
        <div class="dpr-pdf-page">
        <h3 style="margin: 0 0 8px;">5. ROB Status</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Commodity</th>
              <th style="${commonHeadCell}">Opening Stock</th>
              <th style="${commonHeadCell}">Received</th>
              <th style="${commonHeadCell}">Del. to Other Vessel</th>
              <th style="${commonHeadCell}">Del. to Platforms</th>
              <th style="${commonHeadCell}">Consumed by Vessel</th>
              <th style="${commonHeadCell}">Closing Stock</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.robStatus || []).map((row) => [
                safe(row.commodity),
                safe(row.openingStock),
                safe(row.received),
                safe(row.deliveredToOtherVessel),
                safe(row.deliveredToPlatforms),
                safe(row.consumedByVessel),
                safe(row.closingStock),
              ]),
              7,
              'No ROB status rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">5.1 Lube Oil Status</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Grades</th>
              <th style="${commonHeadCell}">Opening Stock</th>
              <th style="${commonHeadCell}">Received</th>
              <th style="${commonHeadCell}">Consumed by Vessel</th>
              <th style="${commonHeadCell}">Closing Stock</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.lubeOilStatusRows || []).map((row) => [
                safe(row.machinery),
                safe(row.openingStock),
                safe(row.received),
                safe(row.consumedByVessel),
                safe(resolveLubeClosingStock(row)),
                safe(resolveLubeClosingStock(row)),
              ]),
              5,
              'No lube oil status rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">6. Weather</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Local Time</th>
              <th style="${commonHeadCell}">Wind Force</th>
              <th style="${commonHeadCell}">Wind Spd</th>
              <th style="${commonHeadCell}">Direction</th>
              <th style="${commonHeadCell}">Sea/Swell Ht</th>
              <th style="${commonHeadCell}">Swell Direction</th>
              <th style="${commonHeadCell}">Current Rate</th>
              <th style="${commonHeadCell}">Current Direction</th>
              <th style="${commonHeadCell}">Baro</th>
              <th style="${commonHeadCell}">Sky</th>
              <th style="${commonHeadCell}">Vis</th>
              <th style="${commonHeadCell}">Temp</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.weatherRows || []).map((row) => [
                safe(row.localTime),
                safe(row.windForce),
                safe(row.windSpd),
                safe(row.direction),
                safe(row.seaSwellHt),
                safe(row.swellDirection),
                safe(row.currentRate),
                safe(row.currentDirection),
                safe(row.baro),
                safe(row.sky),
                safe(row.vis),
                safe(row.temp),
              ]),
              12,
              'No weather rows'
            )}
          </tbody>
        </table>
        <table style="${commonTableStyle}">
          <tbody>
            <tr><td style="font-weight:700;">AM-LW</td><td>${safe(item.tides?.amLw)}</td><td style="font-weight:700;">AM-HW</td><td>${safe(item.tides?.amHw)}</td></tr>
            <tr><td style="font-weight:700;">PM-LW</td><td>${safe(item.tides?.pmLw)}</td><td style="font-weight:700;">PM-HW</td><td>${safe(item.tides?.pmHw)}</td></tr>
            <tr><td style="font-weight:700;">AM-LW Height</td><td>${safe(item.tides?.amLwHeight)}</td><td style="font-weight:700;">AM-HW Height</td><td>${safe(item.tides?.amHwHeight)}</td></tr>
            <tr><td style="font-weight:700;">PM-LW Height</td><td>${safe(item.tides?.pmLwHeight)}</td><td style="font-weight:700;">PM-HW Height</td><td>${safe(item.tides?.pmHwHeight)}</td></tr>
            <tr><td style="font-weight:700;">Sunrise</td><td>${safe(item.tides?.sunrise)}</td><td style="font-weight:700;">Sunset</td><td>${safe(item.tides?.sunset)}</td></tr>
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">7. Downtime</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">From</th>
              <th style="${commonHeadCell}">To</th>
              <th style="${commonHeadCell}">Today</th>
              <th style="${commonHeadCell}">Previous</th>
              <th style="${commonHeadCell}">Total</th>
              <th style="${commonHeadCell}">Reasons / Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.downtimeRows || []).map((row) => [
                safe(row.from),
                safe(row.to),
                safe(row.today),
                safe(row.previous),
                safe(row.total),
                safe(row.reasonsRemarks),
              ]),
              6,
              'No downtime rows'
            )}
          </tbody>
        </table>

        </div>
        <div class="dpr-pdf-page">
        <h3 style="margin: 0 0 8px;">8. POB</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">S.No</th>
              <th style="${commonHeadCell}">Company</th>
              <th style="${commonHeadCell}">Previous</th>
              <th style="${commonHeadCell}">Pax-In</th>
              <th style="${commonHeadCell}">Pax-Out</th>
              <th style="${commonHeadCell}">Total</th>
              <th style="${commonHeadCell}">Breakfast</th>
              <th style="${commonHeadCell}">Lunch</th>
              <th style="${commonHeadCell}">Dinner</th>
              <th style="${commonHeadCell}">Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.pobRows || []).map((row) => [
                safe(row.sNo),
                safe(row.company),
                safe(row.previous),
                safe(row.paxIn),
                safe(row.paxOut),
                safe(row.total),
                safe(row.mealsBreakfast),
                safe(row.mealsLunch),
                safe(row.mealsDinner),
                safe(row.remarks),
              ]),
              10,
              'No POB rows'
            )}
            <tr>
              <td style="border:1px solid #cfd4dc; padding:6px;"></td>
              <td style="border:1px solid #cfd4dc; padding:6px; font-weight:700;">Total</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.previous)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.paxIn)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.paxOut)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.total)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.breakfast)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.lunch)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;">${safe(rowTotals.dinner)}</td>
              <td style="border:1px solid #cfd4dc; padding:6px;"></td>
            </tr>
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">9. Statistics / Safety</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">KPI's</th>
              <th style="${commonHeadCell}">Prev Day</th>
              <th style="${commonHeadCell}">Today</th>
              <th style="${commonHeadCell}">Cumulative</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.safetyLeft || []).map((row) => [
                safe(row.particulars),
                safe(row.previousDay),
                safe(row.today),
                safe(row.cumulative),
              ]),
              4,
              'No left safety rows'
            )}
          </tbody>
        </table>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Meetings & Inspections</th>
              <th style="${commonHeadCell}">Prev Day</th>
              <th style="${commonHeadCell}">Today</th>
              <th style="${commonHeadCell}">Cumulative</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.safetyRight || []).map((row) => [
                safe(row.particulars),
                safe(row.previousDay),
                safe(row.today),
                safe(row.cumulative),
              ]),
              4,
              'No right safety rows'
            )}
          </tbody>
        </table>
        </div>
        <div class="dpr-pdf-page">
        <h3 style="margin: 0 0 8px;">Key Equipment Status</h3>
        <table style="${commonTableStyle} page-break-inside: avoid; break-inside: avoid;">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Equipment</th>
              <th style="${commonHeadCell}">Standby (Y/N)</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.equipmentRows || []).map((row) => [safe(row.equipment), safe(row.standbyYN)]),
              2,
              'No equipment rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">10. Passenger List</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Sr.No</th>
              <th style="${commonHeadCell}">Name</th>
              <th style="${commonHeadCell}">Company</th>
              <th style="${commonHeadCell}">Position</th>
              <th style="${commonHeadCell}">DOJ</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.passengerRows || []).map((row) => [
                safe(row.srNo),
                safe(row.name),
                safe(row.company),
                safe(row.position),
                safe(row.doj),
              ]),
              5,
              'No passenger rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">11. Engine Running Hours</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Label</th>
              <th style="${commonHeadCell}">Opening R/Hr</th>
              <th style="${commonHeadCell}">Daily R/Hr</th>
              <th style="${commonHeadCell}">Total</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.engineRunningHours || []).map((row) => [
                safe(row.label),
                safe(row.openingHours),
                safe(row.dailyHours),
                safe(row.totalHours),
              ]),
              4,
              'No engine running hour rows'
            )}
          </tbody>
        </table>

        <h3 style="margin: 0 0 8px;">12. Engine Parameters</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Label</th>
              <th style="${commonHeadCell}">LO Pres</th>
              <th style="${commonHeadCell}">JCW Cooler</th>
              <th style="${commonHeadCell}">LO Temp Cooler</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.engineTemp?.params || []).map((row) => [
                safe(row.label),
                safe(row.loPres),
                safe(row.jcwCooler),
                safe(row.loTempCooler),
              ]),
              4,
              'No engine parameter rows'
            )}
          </tbody>
        </table>

        </div>

        <!-- Page 5: Section 13 Exhaust Temps -->
        <div class="dpr-pdf-page">
        <h3 style="margin: 0 0 8px;">13. Exhaust Temps</h3>
        <table style="${commonTableStyle}">
          <thead>
            <tr>
              <th style="${commonHeadCell}">Label</th>
              <th style="${commonHeadCell}">1</th>
              <th style="${commonHeadCell}">2</th>
              <th style="${commonHeadCell}">3</th>
              <th style="${commonHeadCell}">4</th>
              <th style="${commonHeadCell}">5</th>
              <th style="${commonHeadCell}">6</th>
              <th style="${commonHeadCell}">7</th>
              <th style="${commonHeadCell}">8</th>
              <th style="${commonHeadCell}">Min</th>
            </tr>
          </thead>
          <tbody>
            ${buildRows(
              (item.engineTemp?.exhTemps || []).map((row) => [
                safe(normalizeExhTempLabel(row.label)),
                safe(row.unit1),
                safe(row.unit2),
                safe(row.unit3),
                safe(row.unit4),
                safe(row.unit5),
                safe(row.unit6),
                safe(row.unit7),
                safe(row.unit8),
                safe(getExhTempMinValue(row)),
              ]),
              10,
              'No exhaust temperature rows'
            )}
          </tbody>
        </table>
        </div>
      `

      const html2pdf = await import('html2pdf.js')
      const safeDate = String(item.reportDate || 'report').replace(/[^\w-]/g, '_')
      const fileName = `dpr_${safeDate}_${item.id}.pdf`

      await html2pdf
        .default()
        .from(pdfContainer)
        .set({
          margin: 0.4,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .save()
    } catch (err) {
      console.error('Failed to download DPR PDF:', err)
      toast.error('Failed to download DPR as PDF. Please try again.', { position: 'top-center' })
    }
  }

  const handleSave = async (record: DailyPositionReportRecord) => {
    try {
      const resolvedVesselId = record.vesselId || currentUser?.vessel?.id
      
      // Validate that vesselId is present and valid
      if (!resolvedVesselId || Number(resolvedVesselId) <= 0) {
        toast.error('Please select a vessel before saving the DPR.', { position: 'top-center' })
        return
      }

      const payload = {
        reportDate: record.reportDate,
        vesselId: resolvedVesselId,
        vesselName: record.vesselName,
        voyageNo: record.voyageNo,
        portOrSea: record.portOrSea,
        fromLocation: record.fromLocation,
        toLocation: record.toLocation,
        eta: record.eta,
        draftFwd: record.draftFwd,
        draftAft: record.draftAft,
        weather: record.weather,
        wind: record.wind,
        seaState: record.seaState,
        dlDate: record.dlDate,
        dlTime: record.dlTime,
        dlLocation: record.dlLocation,
        dlPosition: record.dlPosition,
        activitySummary: record.activitySummary,
        tides: record.tides,
        status: record.status,
        createdByRank: record.createdByRank || roleName || roleType || currentUser?.rank?.rank || 'Master', 
        foConsumptionRows: record.foConsumptionRows,
        machineryFoConsumptionRows: record.machineryFoConsumptionRows,
        activities: record.activities.map((a) => ({
          localTimeFrom: a.localTimeFrom,
          localTimeTo: a.localTimeTo,
          description: a.description,
          position: a.position,
        })),
        robStatus: record.robStatus,
        lubeOilStatusRows: record.lubeOilStatusRows,
        weatherRows: record.weatherRows,
        downtimeRows: record.downtimeRows.map((d) => ({
          from: d.from,
          to: d.to,
          today: d.today,
          previous: d.previous,
          total: d.total,
          reasonsRemarks: d.reasonsRemarks,
        })),
        pobRows: record.pobRows,
        safetyLeft: record.safetyLeft,
        safetyRight: record.safetyRight,
        equipmentRows: record.equipmentRows,
        passengerRows: record.passengerRows,
        engineParamRows: record.engineTemp?.params ?? [],
        exhTempRows: record.engineTemp?.exhTemps ?? [],
        engineRunningHours: record.engineRunningHours,
        movementReport: record.movementReport,
      }

      console.log('save payload', payload)

      const editId = editData?.id
      const isEditing = Boolean(editId)
      let response: any

      if (isEditing && editId !== undefined) {
        response = await updateDpr(editId, payload)
      } else {
        response = await createDpr(payload)
      }

      const persistedId = Number(response?.id ?? editId ?? record.id)
      if (Number.isFinite(persistedId) && persistedId > 0 && currentUserId > 0) {
        if (record.status === 'DRAFT') {
          localStorage.setItem(draftOwnerStorageKey(persistedId), String(currentUserId))
        } else {
          localStorage.removeItem(draftOwnerStorageKey(persistedId))
        }
      }

      console.log('edit response', response)

      toast.success(
        isEditing
          ? (record.status === 'DRAFT'
              ? 'DPR draft updated successfully.'
              : 'DPR updated successfully.')
          : (record.status === 'DRAFT'
              ? 'DPR saved as draft successfully.'
              : 'DPR submitted successfully.'),
        { position: 'top-center' }
      )
      await fetchReports()
      setIsAddOpen(false)
      setEditData(null)
    } catch (err: any) {
      console.error('Failed to save DPR:', err)
      toast.error(getApiErrorMessage(err, 'Failed to save DPR. Please try again.'), { position: 'top-center' })
    }
  }

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  if (!permissionsLoaded) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Daily Progress Reports</PageTitle>
        <div className='card m-5'>
          <div className='card-body text-muted'>Loading Daily Progress Report permissions...</div>
        </div>
      </>
    )
  }

  if (!canReadDpr) {
    return (
      <div className='alert alert-warning m-5'>
        You do not have permission to view Daily Progress Reports (READ_DAILY_PROGRESS_REPORT).
      </div>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Daily Progress Reports</PageTitle>
      <ToastContainer
        position='top-center'
        autoClose={3000}
        newestOnTop
        closeOnClick
        draggable
        pauseOnHover
        style={{ top: '5rem' }}
      />

      <div className='card flex-column-fluid d-flex flex-column bg-white' style={{ flex: 1 }}>
        {/* Header and Controls */}
        <div className='d-flex gap-3 pe-5 border-top'>
          <div className='py-3 ms-3' style={{ flex: 5 }}>
            <h3 className='card-title fw-bold text-dark mb-5'>Daily Progress Reports</h3>
            <div className='d-flex justify-content-between mb-5'>
              <div className='d-flex align-items-center gap-2'>
                <input
                  type='text'
                  className='form-control cp_search_input'
                  placeholder='Search by vessel, voyage, date…'
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                />

                <select
                  className='form-select ms-2'
                  style={{ width: '150px', minWidth: '150px', flexShrink: 0 }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value='ALL'>All Status</option>
                  <option value='DRAFT'>Draft</option>
                  <option value='SUBMITTED'>Submitted</option>
                </select>
              </div>
              <div className='d-flex align-items-center gap-4'>
                {canAddDpr && (
                  <button className='btn btn_primary' onClick={handleAdd}>
                    <i className='bi bi-plus-lg me-1' /> Add DPR
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className='report-table table-responsive' style={{ maxHeight: '50rem', overflowY: 'auto' }}>
              <table className='table table-bordered align-middle'>
                <thead className='table-header text-start'>
                  <tr>
                    <th className='w-50px'>SR.NO</th>
                    <th className='cursor-pointer' onClick={() => handleSort('reportDate')}>
                      REPORT DATE
                      <KTSVG 
                        path={`/media/map/sort-col-${sortConfig.key === 'reportDate' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} 
                        className='ms-2' 
                      />
                    </th>
                    <th className='cursor-pointer' onClick={() => handleSort('vesselName')}>
                      NAME
                      <KTSVG 
                        path={`/media/map/sort-col-${sortConfig.key === 'vesselName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} 
                        className='ms-2' 
                      />
                    </th>
                    <th className='cursor-pointer' onClick={() => handleSort('createdByRank')}>
                      RANK
                      <KTSVG 
                        path={`/media/map/sort-col-${sortConfig.key === 'createdByRank' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} 
                        className='ms-2' 
                      />
                    </th>
                    <th className='cursor-pointer' onClick={() => handleSort('status')}>
                      STATUS
                      <KTSVG 
                        path={`/media/map/sort-col-${sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} 
                        className='ms-2' 
                      />
                    </th>
                    <th className='text-end'>ACTION</th>
                  </tr>
                </thead>
                <tbody className='table-body text-start'>
                  {loading ? (
                    <TableSkeleton rows={8} cols={6} />
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={6} className='text-center text-muted py-10'>
                        {reports.length === 0
                          ? 'No daily progress reports yet. Click "Add DPR" to create one.'
                          : 'No matching records found.'}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td className='text-nowrap'>{formatDateValues(item.reportDate)}</td>
                        <td className='fw-bold text-primary'>
                          <VesselLandingLink vesselId={item.vesselId} label={item.vesselName} className='text-primary text-decoration-underline' />
                        </td>
                        <td>{item.createdByRank || 'Master'}</td>
                        <td>{renderStatusBadge(item.status)}</td>
                        <td className='text-end'>
                          {canDownloadDpr && (
                          <button
                            className='btn btn-sm btn-icon btn-light-success me-1'
                            title='Download'
                            onClick={() => handleReportDownload(item)}
                          >
                            <i className='bi bi-download' />
                          </button>
                          )}
                          {canReadDpr && (
                          <button
                            className='btn btn-sm btn-icon btn-light-primary me-1'
                            title='View'
                            onClick={() => handleView(item)}
                          >
                            <i className='bi bi-eye' />
                          </button>
                          )}
                          {canEditDpr && (
                            <button
                              className='btn btn-sm btn-icon btn-light-warning me-1'
                              title='Edit'
                              onClick={() => handleEdit(item)}
                            >
                              <i className='bi bi-pencil' />
                            </button>
                          )}
                          {canDeleteReport && (
                            <button
                              className='btn btn-sm btn-icon btn-light-danger'
                              title='Delete'
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className='bi bi-trash' />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {reports.length > 0 && (
                <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value))
                        setCurrentPage(1)
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                      Showing{' '}
                      <strong>
                        {(currentPage - 1) * rowsPerPage + 1}-
                        {Math.min(currentPage * rowsPerPage, processed.length)}
                      </strong>{' '}
                      of <strong>{processed.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor: page === currentPage ? '#F4F9FF' : 'transparent',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                minWidth: '40px',
                                borderRadius: '6px',
                                outline: 'none',
                                boxShadow: 'none',
                              }}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <AddDprModal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false)
          setEditData(null)
        }}
        onSave={handleSave}
        editData={editData}
        previousDpr={reports.length > 0 ? reports[0] : null}
        selectedVesselId={selectedVesselId}
        selectedVesselName={selectedVesselName}
        vesselOptions={allVesselOptions}
      />

      <ViewDprModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false)
          setViewData(null)
        }}
        data={viewData}
      />
    </>
  )
}

export default DailyPositionReport
